/**
 * AI Data Consent Modal — AI Transparency & Data Privacy Disclosure
 *
 * Presented to users before any data is transmitted to third-party AI services.
 * Discloses: what data is shared, who receives it, and what is never shared.
 *
 * Version 2.0 — Alibaba Qwen removed; only US-based providers (Groq, Google Gemini).
 */
import React from 'react';
import ReactDOM from 'react-dom';
import { Shield, Brain, Eye, ExternalLink, Check, X, Lock } from 'lucide-react';

const AI_CONSENT_KEY = 'calobite_ai_consent_accepted';
const AI_CONSENT_VERSION = '2.0'; // Bumped: re-prompt existing users with updated disclosure

/**
 * Check if AI consent has been given for the current disclosure version
 */
export const hasAIConsent = () => {
    try {
        const consent = localStorage.getItem(AI_CONSENT_KEY);
        if (!consent) return false;
        const parsed = JSON.parse(consent);
        return parsed.accepted === true && parsed.version === AI_CONSENT_VERSION;
    } catch {
        return false;
    }
};

/**
 * Check if the user has made ANY consent decision (accepted OR declined)
 * for the current version. Returns false only when no decision has been recorded.
 */
export const hasAIConsentDecision = () => {
    try {
        const consent = localStorage.getItem(AI_CONSENT_KEY);
        if (!consent) return false;
        const parsed = JSON.parse(consent);
        return parsed.version === AI_CONSENT_VERSION; // any decision for this version
    } catch {
        return false;
    }
};

/**
 * Save AI consent with timestamp and version
 */
export const saveAIConsent = () => {
    localStorage.setItem(AI_CONSENT_KEY, JSON.stringify({
        accepted: true,
        version: AI_CONSENT_VERSION,
        timestamp: new Date().toISOString()
    }));
};

/**
 * Save a declined AI consent decision.
 * Persisting the declined decision prevents repeated re-prompting on every cold launch.
 */
export const saveAIConsentDeclined = () => {
    localStorage.setItem(AI_CONSENT_KEY, JSON.stringify({
        accepted: false,
        version: AI_CONSENT_VERSION,
        timestamp: new Date().toISOString()
    }));
};

/**
 * Revoke AI consent — disables all AI-powered features
 */
export const revokeAIConsent = () => {
    localStorage.removeItem(AI_CONSENT_KEY);
};

const AIConsentModal = ({ isOpen, onAccept, onDecline }) => {
    if (!isOpen) return null;

    const handleAccept = () => {
        saveAIConsent();
        onAccept();
    };

    const handleDecline = () => {
        saveAIConsentDeclined();
        onDecline();
    };

    return ReactDOM.createPortal(
        <div className="fixed inset-0 bg-black/90 backdrop-blur-md z-[9999] flex items-start justify-center p-4 overflow-y-auto">
            <div className="bg-gradient-to-b from-slate-900 to-slate-950 w-full max-w-md rounded-3xl border border-white/10 shadow-2xl my-6">

                {/* Header */}
                <div className="p-6 border-b border-white/10 text-center">
                    <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center mx-auto mb-4 shadow-lg shadow-blue-500/30">
                        <Brain className="w-8 h-8 text-white" />
                    </div>
                    <h2 className="text-xl font-bold text-white mb-1">AI-Powered Features</h2>
                    <p className="text-sm text-gray-400">Your permission is required before CaloBite uses AI analysis</p>
                </div>

                {/* Content */}
                <div className="p-6 space-y-4">

                    {/* What IS shared */}
                    <div className="bg-blue-500/10 rounded-xl p-4 border border-blue-500/20">
                        <div className="flex items-center gap-2 mb-3">
                            <Eye className="w-4 h-4 text-blue-400" />
                            <span className="text-sm font-bold text-white">What data is sent to AI</span>
                        </div>
                        <ul className="space-y-2 text-xs text-gray-300">
                            <li className="flex items-start gap-2">
                                <span className="text-blue-400 mt-0.5 flex-shrink-0">•</span>
                                <span><strong className="text-white">Food photos & descriptions</strong> — for nutritional analysis and calorie estimation</span>
                            </li>
                            <li className="flex items-start gap-2">
                                <span className="text-blue-400 mt-0.5 flex-shrink-0">•</span>
                                <span><strong className="text-white">Health metrics</strong> — age (integer only), gender, weight, height for metabolic calculations</span>
                            </li>
                            <li className="flex items-start gap-2">
                                <span className="text-blue-400 mt-0.5 flex-shrink-0">•</span>
                                <span><strong className="text-white">Fitness preferences</strong> — workout location, duration, intensity, and exercise goals</span>
                            </li>
                            <li className="flex items-start gap-2">
                                <span className="text-blue-400 mt-0.5 flex-shrink-0">•</span>
                                <span><strong className="text-white">Dietary information</strong> — allergens, cuisine preferences, and dietary restrictions you have specified</span>
                            </li>
                            <li className="flex items-start gap-2">
                                <span className="text-blue-400 mt-0.5 flex-shrink-0">•</span>
                                <span><strong className="text-white">Today's wellness logs</strong> — meal names and macros, exercise sessions (metabolic scheduling feature only)</span>
                            </li>
                        </ul>
                    </div>

                    {/* What is NEVER shared */}
                    <div className="bg-green-500/10 rounded-xl p-4 border border-green-500/20">
                        <div className="flex items-center gap-2 mb-3">
                            <Lock className="w-4 h-4 text-green-400" />
                            <span className="text-sm font-bold text-green-400">What is NEVER shared with AI</span>
                        </div>
                        <ul className="space-y-1.5 text-xs text-gray-300">
                            {[
                                'Your name or display name',
                                'Your email address',
                                'Your account ID or user identifier',
                                'Your payment information',
                                'Your precise location',
                                'Your password or authentication tokens',
                            ].map((item, i) => (
                                <li key={i} className="flex items-center gap-2">
                                    <div className="w-4 h-4 rounded-full bg-green-500/20 flex items-center justify-center flex-shrink-0">
                                        <X className="w-2.5 h-2.5 text-green-400" />
                                    </div>
                                    <span>{item}</span>
                                </li>
                            ))}
                        </ul>
                        <p className="text-[10px] text-gray-500 mt-2 italic">
                            AI providers receive only the functional health data required to generate your analysis — never anything that identifies you as an individual.
                        </p>
                    </div>

                    {/* Who receives it */}
                    <div className="bg-slate-800/50 rounded-xl p-4 border border-white/5">
                        <div className="flex items-center gap-2 mb-3">
                            <ExternalLink className="w-4 h-4 text-purple-400" />
                            <span className="text-sm font-bold text-white">AI service providers</span>
                        </div>
                        <p className="text-xs text-gray-400 mb-3">Your data is processed exclusively by these US-based enterprise AI services:</p>
                        <div className="grid grid-cols-2 gap-3">
                            <div className="bg-orange-500/10 rounded-xl p-3 border border-orange-500/20">
                                <div className="text-sm font-bold text-orange-400 mb-0.5">Groq</div>
                                <div className="text-[10px] text-gray-400">Llama 3.3 · USA 🇺🇸</div>
                                <div className="text-[10px] text-gray-500 mt-1">SOC 2 Type II · No model training on API data</div>
                            </div>
                            <div className="bg-blue-500/10 rounded-xl p-3 border border-blue-500/20">
                                <div className="text-sm font-bold text-blue-400 mb-0.5">Google Gemini</div>
                                <div className="text-[10px] text-gray-400">Gemini 2.5 Flash · USA 🇺🇸</div>
                                <div className="text-[10px] text-gray-500 mt-1">GDPR / CCPA · No model training on API data</div>
                            </div>
                        </div>
                    </div>

                    {/* Privacy promise */}
                    <div className="bg-slate-800/40 rounded-xl p-3 border border-white/5">
                        <ul className="space-y-1.5 text-xs text-gray-300">
                            <li className="flex items-start gap-2">
                                <Check className="w-3 h-3 text-green-400 mt-0.5 flex-shrink-0" />
                                <span>Neither AI provider uses your data to train or improve their models</span>
                            </li>
                            <li className="flex items-start gap-2">
                                <Check className="w-3 h-3 text-green-400 mt-0.5 flex-shrink-0" />
                                <span>You can revoke consent at any time in <strong className="text-white">Settings → Privacy</strong></span>
                            </li>
                            <li className="flex items-start gap-2">
                                <Check className="w-3 h-3 text-green-400 mt-0.5 flex-shrink-0" />
                                <span>If you decline, all core tracking features remain available — only AI-generated analysis is disabled</span>
                            </li>
                        </ul>
                    </div>
                </div>

                {/* Footer */}
                <div className="p-6 pt-0 space-y-3">
                    <button
                        onClick={handleAccept}
                        className="w-full py-4 bg-gradient-to-r from-blue-500 to-purple-600 rounded-xl text-white font-bold text-sm hover:scale-[1.02] transition-transform shadow-lg"
                    >
                        I Understand & Accept
                    </button>
                    <button
                        onClick={handleDecline}
                        className="w-full py-3 bg-slate-800 rounded-xl text-gray-400 font-medium text-sm hover:bg-slate-700 transition-colors"
                    >
                        Decline — Disable AI Features
                    </button>
                    <p className="text-center text-[10px] text-gray-600">
                        See our{' '}
                        <a href="/privacy" className="underline text-gray-400 hover:text-white transition-colors">
                            Privacy Policy
                        </a>{' '}
                        for the complete data handling disclosure
                    </p>
                </div>
            </div>
        </div>,
        document.body
    );
};

export default AIConsentModal;
